import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssignWorkerRequest,
  BomMaterialSwapRequest,
  BomMaterialUsesQuery,
  CreateProjectRequest,
  DuplicateProjectRequest,
  ListProjectsQuery,
  MergeProjectsRequest,
  ProjectDetails,
  TransitionProjectStatusRequest,
  UpdateBomCoefficientRequest,
  UpdateProjectRequest,
} from '@kreart/shared';
import { projectsApi } from './api';
import { invalidateProjectPlanningQueries } from '@/features/project-planning/hooks';
import { operationsSchedulerQueryKeys } from '@/features/operations-scheduler/hooks';
import { invalidateProjectTender } from '@/features/project-organization/hooks';

const ROOT = ['projects'] as const;
export const projectsQueryKeys = {
  all: ROOT,
  list: (q: ListProjectsQuery) => [...ROOT, 'list', q] as const,
  detail: (id: string) => [...ROOT, 'detail', id] as const,
  assignments: (id: string) => [...ROOT, 'assignments', id] as const,
  // BOM aggregate. Project-wide when roomId is omitted; per-room otherwise.
  // Both forms share the `['project-bom', projectId]` prefix so a single
  // prefix invalidation refreshes every scoped BOM view.
  bom: (projectId: string, roomId?: string) =>
    roomId ? (['project-bom', projectId, roomId] as const) : (['project-bom', projectId] as const),
};

/**
 * Invalidate the live BOM aggregate for a project so it is recomputed after any
 * line-item change (cabinet / panel / countertop / drawer / element / hardware /
 * lighting add / edit / delete). Prefix-matches both the project-wide and every
 * per-room BOM query.
 *
 * `refetchType: 'all'` refetches INACTIVE observers too (not just mounted ones),
 * so a BOM view that was cached before an in-place edit is refreshed immediately
 * rather than only on its next mount — closing the stale-after-edit window.
 * Returns the promise so callers may await the refetch when they need to.
 *
 * Also refreshes the project's tender: its materials cost IS this BOM total,
 * and a line-item write moves Project.totalNet too — which is the base for all
 * six tender percentages. Neither is derivable client-side, so invalidation is
 * the only tool. Doing it here covers all seven line-item features at once.
 */
export function invalidateProjectBom(qc: ReturnType<typeof useQueryClient>, projectId: string) {
  return Promise.all([
    qc.invalidateQueries({
      queryKey: projectsQueryKeys.bom(projectId),
      refetchType: 'all',
    }),
    invalidateProjectTender(qc, projectId),
  ]);
}

/**
 * Every line-item list's query root.
 *
 * Each feature owns its own root rather than sharing a `project-items` prefix,
 * so a change that re-prices the whole project has to name all seven — a
 * prefix invalidation would silently miss every one of them.
 */
const LINE_ITEM_QUERY_ROOTS = [
  ['project-cabinets'],
  ['project-panels'],
  ['project-countertops'],
  ['project-elements'],
  ['project-lighting'],
  ['project-drawers'],
  ['project-cabinet-hardware'],
] as const;

/**
 * Inline BOM markup edit — writes one coefficient onto every use behind the row.
 *
 * The server re-prices the project in the same transaction, so this invalidates
 * far more than the BOM: the row's own markup, every line item's unit price, and
 * the project totals all moved. `invalidateProjectBom` already covers the BOM
 * and the tender; the detail query is what the header reads its totals from, and
 * the line-item lists are what the user sees change underneath them.
 */
export function useSetBomCoefficient(projectId: string, roomId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<UpdateBomCoefficientRequest, 'roomId'>) =>
      projectsApi.setBomCoefficient(projectId, { ...body, ...(roomId ? { roomId } : {}) }),
    // The cell surfaces its own error inline, so no global toast.
    meta: { silent: true },
    onSuccess: async () => {
      await Promise.all([
        invalidateProjectBom(qc, projectId),
        qc.invalidateQueries({ queryKey: projectsQueryKeys.detail(projectId) }),
        ...LINE_ITEM_QUERY_ROOTS.map((root) =>
          qc.invalidateQueries({ queryKey: root, refetchType: 'all' }),
        ),
      ]);
    },
  });
}

/**
 * The uses behind one BOM material row, for the BOM's "edit material" dialog.
 * Keyed on the previewed replacement too, so picking a board refetches the
 * edge outcome — and keeps showing the previous table meanwhile.
 */
export function useBomMaterialUses(projectId: string, query: BomMaterialUsesQuery | null) {
  return useQuery({
    queryKey: ['project-bom', projectId, 'material-uses', query] as const,
    queryFn: () => projectsApi.bomMaterialUses(projectId, query as BomMaterialUsesQuery),
    enabled: Boolean(query),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

/**
 * Swap a board across the chosen uses. Moves materials, edges and prices, so
 * everything that shows any of the three is refreshed — the same set the BOM
 * coefficient edit refreshes.
 */
export function useSwapBomMaterial(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BomMaterialSwapRequest) => projectsApi.swapBomMaterial(projectId, body),
    meta: { silent: true },
    onSuccess: async () => {
      await Promise.all([
        invalidateProjectBom(qc, projectId),
        qc.invalidateQueries({ queryKey: projectsQueryKeys.detail(projectId) }),
        ...LINE_ITEM_QUERY_ROOTS.map((root) =>
          qc.invalidateQueries({ queryKey: root, refetchType: 'all' }),
        ),
      ]);
    },
  });
}

export function useProjects(query: ListProjectsQuery, enabled = true) {
  return useQuery({
    queryKey: projectsQueryKeys.list(query),
    queryFn: () => projectsApi.list(query),
    placeholderData: keepPreviousData,
    enabled,
    meta: { silent: true },
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: id ? projectsQueryKeys.detail(id) : ['projects', 'detail', 'nil'],
    queryFn: () => projectsApi.get(id!),
    enabled: Boolean(id),
    meta: { silent: true },
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProjectRequest) => projectsApi.create(body),
    meta: { silent: true },
    onSuccess: (data: ProjectDetails) => {
      qc.setQueryData(projectsQueryKeys.detail(data.id), data);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

/**
 * The source project's material slots, fetched when the clone dialog's
 * "Materiale" section is first opened — not on dialog open, so a plain retitle
 * never waits on it.
 */
export function useProjectMaterialUsage(projectId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...ROOT, 'material-usage', projectId ?? ''],
    queryFn: () => projectsApi.materialUsage(projectId as string),
    enabled: Boolean(projectId) && enabled,
    staleTime: 30_000,
    meta: { silent: true },
  });
}

/**
 * Copy a project with everything in it. Separate from useCreateProject because
 * the server does the copying: the body only carries the header fields the
 * dialog collected, and the rooms, line items and tender settings follow from
 * the source id.
 */
export function useDuplicateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, body }: { sourceId: string; body: DuplicateProjectRequest }) =>
      projectsApi.duplicate(sourceId, body),
    meta: { silent: true },
    onSuccess: (data: ProjectDetails) => {
      qc.setQueryData(projectsQueryKeys.detail(data.id), data);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

/**
 * Bring several projects together into a new one. The originals leave the list as
 * part of it, so their cached details go too — the same as deleting them.
 */
export function useMergeProjects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MergeProjectsRequest) => projectsApi.merge(body),
    meta: { silent: true },
    onSuccess: (data: ProjectDetails, body: MergeProjectsRequest) => {
      qc.setQueryData(projectsQueryKeys.detail(data.id), data);
      for (const id of body.projectIds) {
        qc.removeQueries({ queryKey: projectsQueryKeys.detail(id) });
      }
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useUpdateProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProjectRequest) => projectsApi.update(id, body),
    meta: { silent: true },
    onSuccess: (data: ProjectDetails) => {
      qc.setQueryData(projectsQueryKeys.detail(id), data);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
      invalidateProjectPlanningQueries(qc);
      // The scheduler shows these projects by title and schedules them against
      // their deadline, so an edit here — a moved deadline above all — must not
      // wait for its next poll to be believed.
      void qc.invalidateQueries({ queryKey: operationsSchedulerQueryKeys.all });
    },
  });
}

export function useDeleteProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => projectsApi.remove(id),
    meta: { silent: true },
    onSuccess: () => {
      qc.removeQueries({ queryKey: projectsQueryKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

/** Irreversible; the caller is responsible for confirming first. */
export function usePurgeProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => projectsApi.purge(id),
    meta: { silent: true },
    onSuccess: () => {
      qc.removeQueries({ queryKey: projectsQueryKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useTransitionProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TransitionProjectStatusRequest) => projectsApi.transition(id, body),
    meta: { silent: true },
    onSuccess: (data: ProjectDetails) => {
      qc.setQueryData(projectsQueryKeys.detail(id), data);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useAssignWorker(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignWorkerRequest) => projectsApi.assign(projectId, body),
    meta: { silent: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsQueryKeys.detail(projectId) });
      void qc.invalidateQueries({ queryKey: projectsQueryKeys.assignments(projectId) });
    },
  });
}

export function useUnassignWorker(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) => projectsApi.unassign(projectId, assignmentId),
    meta: { silent: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsQueryKeys.detail(projectId) });
      void qc.invalidateQueries({ queryKey: projectsQueryKeys.assignments(projectId) });
    },
  });
}
